var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { Form, Input, Select, Upload, Button, Typography, message, Card, Segmented } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useStore } from '../../../../store/rootStore';
import { useHistory } from 'react-router-dom';
import YAML from 'yaml';
import ExpertEditorWidget from './ScenarioDraftWizard/ExpertEditorWidget';
const { Option } = Select;
const { Text } = Typography;
const ScenarioCreateView = ({ formRef, onValuesChange, initialValues, events }) => {
    const { t } = useTranslation();
    const availableEvents = Object.values(events || {}).flat();
    const { scenarios } = useStore();
    const history = useHistory();
    const [mode, setMode] = useState('create');
    const [importData, setImportData] = useState(undefined);
    const [importAsStatus, setImportAsStatus] = useState('draft');
    const [isImporting, setIsImporting] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState(null);
    const isImportable = (val) => {
        if (!val || typeof val !== 'object')
            return false;
        const obj = val;
        // Minimal structural checks for safety; backend performs full validation
        return typeof obj.version === 'string' && typeof obj.name === 'string';
    };
    const beforeUpload = (file) => __awaiter(void 0, void 0, void 0, function* () {
        const reader = new FileReader();
        reader.onload = () => {
            const content = String(reader.result || '');
            try {
                const asYaml = YAML.parse(content);
                setImportData(isImportable(asYaml) ? asYaml : undefined);
            }
            catch (_yamlErr) {
                message.error(React.createElement(Trans, { i18nKey: "invalidYamlFile", defaults: "Failed to parse file. Please provide valid YAML." }));
            }
            setUploadedFileName(file.name);
        };
        reader.readAsText(file);
        return false; // prevent auto upload
    });
    const handleImport = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!importData || typeof importData !== 'object' || Object.keys(importData).length === 0) {
            message.warning(React.createElement(Trans, { i18nKey: "importScenarioHelp" }));
            return;
        }
        const request = {
            data: importData,
            asStatus: importAsStatus,
        };
        setIsImporting(true);
        try {
            const created = yield scenarios.importScenario(request);
            message.success(React.createElement(Trans, { i18nKey: "scenarioImported" }));
            history.push(`/manage/scenarios/${created._id}`);
        }
        catch (e) {
            const errorMessage = ((_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || (e === null || e === void 0 ? void 0 : e.message) || t('scenarioImportError', 'Failed to import scenario');
            message.error(errorMessage);
        }
        finally {
            setIsImporting(false);
        }
    });
    return (React.createElement(Form, { layout: "vertical", ref: formRef, onValuesChange: (changed, all) => {
            if (mode === 'create')
                onValuesChange(changed, all);
        }, initialValues: initialValues },
        React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
            React.createElement(Typography.Text, { strong: true },
                React.createElement(Trans, { i18nKey: "scenarioCreation" })),
            React.createElement(Segmented, { options: [
                    { label: React.createElement(Trans, { i18nKey: "createNew" }), value: 'create' },
                    { label: React.createElement(Trans, { i18nKey: "import" }), value: 'import' },
                ], value: mode, onChange: (val) => {
                    if (val === 'create' || val === 'import')
                        setMode(val);
                } })),
        mode === 'import' && (React.createElement(Card, { title: React.createElement(Trans, { i18nKey: "importScenario" }), style: { marginBottom: 16 } },
            React.createElement(Text, { type: "secondary" },
                React.createElement(Trans, { i18nKey: "importScenarioHelp" })),
            React.createElement("div", { style: { marginTop: 12, marginBottom: 12 } },
                React.createElement(Upload, { beforeUpload: beforeUpload, showUploadList: false, accept: ".json,.yml,.yaml" },
                    React.createElement(Button, null,
                        React.createElement(Trans, { i18nKey: "uploadFile" })),
                    uploadedFileName && (React.createElement(Text, { style: { marginLeft: 8 }, type: "secondary" }, uploadedFileName)))),
            React.createElement(ExpertEditorWidget, { schema: {}, value: importData, onChange: (val) => setImportData(isImportable(val) ? val : undefined), disabled: false, readonly: false, height: "250px" }),
            React.createElement("div", { style: { display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 } },
                React.createElement("span", null,
                    React.createElement(Trans, { i18nKey: "importAs" }),
                    ":"),
                React.createElement(Select, { value: importAsStatus, onChange: (v) => setImportAsStatus(v), style: { width: 160 } },
                    React.createElement(Option, { value: "draft" },
                        React.createElement(Trans, { i18nKey: "scenarioDraft", defaults: "Draft" })),
                    React.createElement(Option, { value: "inactive" },
                        React.createElement(Trans, { i18nKey: "importAsInactiveRevision", defaults: "Inactive revision" })),
                    React.createElement(Option, { value: "active" },
                        React.createElement(Trans, { i18nKey: "importAsActiveRevision", defaults: "Active revision" }))),
                React.createElement(Button, { type: "primary", onClick: handleImport, loading: isImporting },
                    React.createElement(Trans, { i18nKey: "import", defaults: "Import" }))))),
        React.createElement(Form.Item, { name: "name", label: React.createElement(Trans, { i18nKey: "scenarioName" }), rules: [{ required: true, message: React.createElement(Trans, { i18nKey: "fieldRequired" }) }], style: { display: mode === 'create' ? 'block' : 'none' } },
            React.createElement(Input, { disabled: mode !== 'create' })),
        React.createElement(Form.Item, { name: "description", label: React.createElement(Trans, { i18nKey: "scenarioDescription" }), style: { display: mode === 'create' ? 'block' : 'none' } },
            React.createElement(Input.TextArea, { disabled: mode !== 'create' })),
        React.createElement(Form.Item, { name: "triggerEvent", label: React.createElement(Trans, { i18nKey: "triggerEvent" }), rules: [{ required: true, message: React.createElement(Trans, { i18nKey: "fieldRequired" }) }], style: { display: mode === 'create' ? 'block' : 'none' } },
            React.createElement(Select, { disabled: mode !== 'create' }, availableEvents.map((event) => (React.createElement(Option, { key: event.name, value: event.name }, event.title)))))));
};
export default ScenarioCreateView;
